export const USER_ROLES = {
  SUPER_ADMIN: 1,
  ADMINISTRATOR: 2,
  CUSTOMER: 3,
} as const;

export const LOGIN_ROLE_VALUES = Object.values(USER_ROLES);

const CUSTOMER_PORTAL_PREFIXES = [
  '/dashboard',
  '/shipments',
  '/available-loads',
  '/profile',
  '/my-bids',
  '/my-books',
];

type RoleContainer = {
  role?: unknown;
  roleValue?: unknown;
  roleCode?: unknown;
  user?: unknown;
};

/** Reads a role from either an API user or the nested NextAuth session user. */
export function getUserRoleValue(value: unknown): number | undefined {
  if (!value || typeof value !== 'object') return undefined;

  const container = value as RoleContainer;
  const role = container.role;
  const roleObject =
    role && typeof role === 'object'
      ? (role as { value?: unknown; id?: unknown; code?: unknown })
      : undefined;
  const candidates = [
    container.roleValue,
    container.roleCode,
    roleObject?.value,
    roleObject?.id,
    roleObject?.code,
    role,
  ];

  for (const candidate of candidates) {
    const normalized = Number(candidate);
    if (Number.isInteger(normalized)) return normalized;
  }

  return getUserRoleValue(container.user);
}

export function canCreateUsers(value: unknown) {
  return canManageUsers(value);
}

export function isCustomer(value: unknown) {
  return getUserRoleValue(value) === USER_ROLES.CUSTOMER;
}

export function canManageUsers(value: unknown) {
  return getUserRoleValue(value) === USER_ROLES.SUPER_ADMIN;
}

export function canAccessBackOffice(value: unknown) {
  const roleValue = getUserRoleValue(value);
  return (
    roleValue === USER_ROLES.SUPER_ADMIN ||
    roleValue === USER_ROLES.ADMINISTRATOR
  );
}

/** Customer accounts are limited to their dashboard, shipments, available loads and profile. */
export function canAccessPortalPath(value: unknown, pathname: string) {
  const roleValue = getUserRoleValue(value);
  if (roleValue === undefined) return false;

  if (roleValue === USER_ROLES.SUPER_ADMIN) return true;

  const normalizedPath =
    `/${pathname}`.replace(/\/{2,}/g, '/').replace(/\/$/, '') || '/';

  if (roleValue === USER_ROLES.ADMINISTRATOR) {
    return !(
      normalizedPath === '/users' || normalizedPath.startsWith('/users/')
    );
  }

  if (roleValue !== USER_ROLES.CUSTOMER) return false;

  return CUSTOMER_PORTAL_PREFIXES.some(
    (prefix) =>
      normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)
  );
}
